import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Share2,
  Tag,
  Maximize2,
  ShieldAlert,
  ArrowRight,
  Facebook,
  Linkedin,
  Instagram,
} from "lucide-react";
import { Twitter } from "lucide-react";
import { http } from "../../axios/axios";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import { Helmet } from "react-helmet-async";

const BlogDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState({});
  const [loading, setLoading] = useState(true);
  const [latestBlogs, setLatestBlogs] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await http.get(`/blogs/slug/${slug}`);
        setBlog(res.data?.data || res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchLatestBlogs = async () => {
      try {
        const res = await http.get("/blogs/published?page=1&limit=3");

        if (res.data.success) {
          const filtered = res.data.data.filter((b) => b.slug !== slug);
          setLatestBlogs(filtered.slice(0, 3));
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchPost();
    fetchLatestBlogs();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }
  if (!blog) {
    return <div>Blog not found</div>;
  }

  const meta = blog?.metaFields?.[0];

  let schema = null;

  try {
    if (blog?.schemaScript) {
      schema =
        typeof blog.schemaScript === "string"
          ? JSON.parse(blog.schemaScript)
          : blog.schemaScript;
    }
  } catch (err) {
    console.error("Invalid schema JSON", err);
  }

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return "";

    try {
      const parsedUrl = new URL(url);

      // youtu.be/VIDEO_ID
      if (parsedUrl.hostname === "youtu.be") {
        return `https://www.youtube.com/embed/${parsedUrl.pathname.slice(1)}`;
      }

      // youtube.com/watch?v=VIDEO_ID
      if (parsedUrl.searchParams.get("v")) {
        return `https://www.youtube.com/embed/${parsedUrl.searchParams.get("v")}`;
      }

      return url;
    } catch (error) {
      return "";
    }
  };
  return (
    <>
      <Helmet>
        <title>{meta?.title || blog.blogHeading}</title>

        <meta
          name="description"
          content={meta?.description || blog.blogShortDescription}
        />

        <meta name="keywords" content={meta?.keywords?.join(", ") || ""} />

        {schema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(schema),
            }}
          />
        )}
      </Helmet>

      <div className="bg-white min-h-screen">
        <section
          className="
    relative
    overflow-hidden
    min-h-[280px]
    h-[40vh]
    md:h-[50vh]
    lg:h-[60vh]
  "
        >
          <figure className="absolute inset-0">
            <img
              src={GetImageUrl(blog?.blogBannerImage)}
              alt={blog?.blogBannerImageAlt}
              className="h-full w-full object-cover"
            />

            <figcaption className="sr-only">
              {blog?.blogBannerImageAlt}
            </figcaption>
          </figure>

          <div className="absolute inset-0 bg-black/60" />

          <header className="relative z-10 flex h-full items-end">
            <div className="w-full px-4 py-10 sm:px-6 sm:py-12 lg:px-10 xl:px-16 lg:py-16 pb-6 sm:pb-8 md:pb-10">
              <div className="max-w-5xl">
                <span className="inline-flex rounded-full bg-blue-600 px-3 py-1 sm:px-4 sm:py-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-white">
                  {blog.blogCategory}
                </span>

                <h1 className="mt-4 font-serif font-bold leading-tight text-white text-2xl sm:text-3xl md:text-[2.5rem] lg:text-5xl xl:text-6xl">
                  {blog.blogHeading}
                </h1>

                <div className="hidden md:block">
                  <p className="mt-5 max-w-3xl text-base lg:text-lg leading-7 lg:leading-8 text-gray-200">
                    {blog.blogShortDescription}
                  </p>

                  <div className="mt-6 flex flex-wrap items-center gap-3 lg:gap-4 text-xs lg:text-sm text-gray-300">
                    <span>{blog.author}</span>

                    <span className="h-1 w-1 rounded-full bg-gray-400" />

                    <span>
                      {new Date(blog.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>

                    <span className="h-1 w-1 rounded-full bg-gray-400" />

                    <span>
                      {Math.max(
                        1,
                        Math.ceil(
                          blog.content
                            ?.map(
                              (c) =>
                                `${c.heading} ${c.shortDescription} ${c.description.replace(
                                  /<[^>]*>/g,
                                  "",
                                )}`,
                            )
                            .join(" ")
                            .split(/\s+/).length / 200,
                        ),
                      )}{" "}
                      min read
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </header>
        </section>

        <section className="px-4 py-10 sm:px-6 sm:py-12 lg:px-10 xl:px-16 lg:py-16">
          <div className=" grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-2 space-y-12 md:space-y-16">
              {blog.content.map((item, index) => (
                <section
                  key={index}
                  id={`section-${index}`}
                  className="scroll-mt-38"
                >
                  <h2 className="text-2xl md:text-3xl font-bold leading-tight">
                    {item.heading}
                  </h2>

                  <p className="mt-3 text-base md:text-lg text-gray-600 leading-7">
                    {item.shortDescription}
                  </p>

                  {item.imageUrl && (
                    <figure className="mt-8 overflow-hidden rounded-lg">
                      <img
                        src={GetImageUrl(item.imageUrl)}
                        alt={item.imageAlt}
                        className="w-full h-56 sm:h-72 lg:h-[480px] object-cover"
                      />
                    </figure>
                  )}

                  {item.youtubeUrl && (
                    <div className="mt-8">
                      <div className="overflow-hidden rounded-lg">
                        <iframe
                          src={getYoutubeEmbedUrl(item.youtubeUrl)}
                          className="block w-full h-56 sm:h-72 lg:h-[480px]"
                          title={item.caption || "YouTube video"}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      {item.caption && (
                        <p className="mt-3 text-center text-sm italic text-gray-500">
                          {item.caption}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-8 whitespace-pre-line text-gray-700 leading-8 text-base md:text-lg  text-justify">
                    {item.description}
                  </div>
                </section>
              ))}
            </div>

            <aside className="lg:col-span-1 lg:sticky lg:top-40 self-start">
              <div className="space-y-8">
                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <h3 className="border-b border-gray-300 pb-4 text-xl font-bold text-center">
                    Blog Details
                  </h3>

                  <div className="mt-6 space-y-6">
                    <div>
                      <p className="text-sm text-gray-500">Author</p>
                      <p className="mt-1 font-semibold text-gray-900">
                        {blog.author}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">Category</p>
                      <p className="mt-1 font-semibold text-gray-900">
                        {blog.blogCategory}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">Published</p>
                      <p className="mt-1 font-semibold text-gray-900">
                        {new Date(blog.createdAt).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <h3 className="border-b border-gray-300 pb-4 text-xl font-bold text-center">
                    Contents
                  </h3>

                  <ul className="mt-6 space-y-4">
                    {blog.content.map((item, index) => (
                      <li key={index}>
                        <a
                          href={`#section-${index}`}
                          className="block text-sm md:text-base text-blue-600 transition-all duration-200 hover:translate-x-1 hover:text-blue-700"
                        >
                          {item.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="px-4 pb-10 sm:px-6 lg:px-10 xl:px-16">
          <div className="border-t border-gray-200 pt-8">
            <div className="mx-auto max-w-3xl rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="text-center text-lg font-bold text-gray-900">
                Follow Us
              </h3>

              <p className="mt-2 text-center text-sm text-gray-500">
                Stay connected with us for more updates and insights.
              </p>

              <div className="mt-4 flex justify-center gap-3">
                {blog.socialLinks?.facebook && (
                  <a
                    href={blog.socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-9 w-9 rounded-full bg-blue-600 text-white flex items-center justify-center hover:scale-110 transition"
                  >
                    <Facebook size={17} />
                  </a>
                )}

                {blog.socialLinks?.x && (
                  <a
                    href={blog.socialLinks.x}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-9 w-9 rounded-full bg-black text-white flex items-center justify-center hover:scale-110 transition"
                  >
                    <Twitter size={17} />
                  </a>
                )}

                {blog.socialLinks?.linkedin && (
                  <a
                    href={blog.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-9 w-9 rounded-full bg-blue-700 text-white flex items-center justify-center hover:scale-110 transition"
                  >
                    <Linkedin size={17} />
                  </a>
                )}

                {blog.socialLinks?.instagram && (
                  <a
                    href={blog.socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-9 w-9 rounded-full bg-pink-600 text-white flex items-center justify-center hover:scale-110 transition"
                  >
                    <Instagram size={17} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
        <section className="px-4 pb-16 sm:px-6 lg:px-10 xl:px-16">
          <div className="border-t border-gray-300 pt-12">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-bold">
                Explore More Articles
              </h2>

              <button
                onClick={() => navigate("/blogs")}
                className="hidden sm:flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                View All
                <ArrowRight size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
              {latestBlogs.map((latest, index) => (
                <article
                  key={latest._id}
                  aria-labelledby={`latest-blog-title-${latest._id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/blogs/${latest.slug}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate(`/blogs/${latest.slug}`);
                    }
                  }}
                  className="group relative h-[280px] sm:h-[320px] lg:h-[360px] overflow-hidden rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  <figure className="relative h-full overflow-hidden">
                    <img
                      src={GetImageUrl(latest.blogBannerImage)}
                      loading={index <= 3 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : "auto"}
                      decoding="async"
                      alt={latest.blogBannerImageAlt || latest.blogHeading}
                      className="absolute inset-0 h-full w-full object-cover brightness-90 transition-all duration-700 group-hover:scale-105"
                    />

                    <figcaption className="sr-only">
                      {latest.blogHeading}
                    </figcaption>
                  </figure>

                  <div className="absolute inset-0 bg-black/10" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />

                  <div className="absolute left-5 top-5">
                    <span className="rounded-md bg-blue-600/90 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md">
                      {latest.blogCategory}
                    </span>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-7">
                    <p className="mb-2 text-sm text-gray-200">
                      By {latest.author}
                    </p>

                    <h3
                      id={`latest-blog-title-${latest._id}`}
                      className="text-lg sm:text-xl 2xl:text-2xl font-bold leading-tight text-white line-clamp-2"
                    >
                      {latest.blogHeading}
                    </h3>

                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-blue-400 transition-colors group-hover:text-white">
                      Read Blog
                      <ArrowRight
                        size={12}
                        className="transition-transform duration-300 group-hover:translate-x-2"
                      />
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <button
                onClick={() => navigate("/blogs")}
                className="inline-flex items-center gap-2 text-blue-600 font-medium"
              >
                View All Blogs
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogDetails;
